/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { ArrowLeft, ArrowRight, Info, Layers, Heart, Music2, Shield, Sun } from 'lucide-react';
import { PageRoute } from '../types';

interface DomiProps {
  setRoute: (route: PageRoute) => void;
}

const profileItems = [
  { icon: Info, label: '名前', value: 'ドミ（Domi）' },
  { icon: Layers, label: '住む世界', value: 'koemori' },
  { icon: Heart, label: '好きなこと', value: 'みんなと歌うこと、いろんな歌声を聴くこと' },
  { icon: Music2, label: 'モチーフ', value: '心地よいハーモニーを奏でる「三度音程（長3度）」の精霊' },
  { icon: Shield, label: '大切にしていること', value: '歌う楽しさ、一人ひとりの歌声' },
  { icon: Sun, label: '夢', value: 'もっとたくさんの「うた友」と一緒に声を重ねること！' },
];

const personalityCards = [
  {
    num: '01',
    title: '歌うこと、そのものが好き！',
    description: '上手い下手なんて関係ない。ただ歌うことの楽しさを、ずっと大切にしていきたいな。',
    image: '/domily/domi-personality-01.webp',
  },
  {
    num: '02',
    title: 'どんな歌声も、特別で素敵。',
    description: '一人ひとりの声には、その人だけの音色がある。すべての歌声がかけがえのない宝物だよ。',
    image: '/domily/domi-personality-02.webp',
  },
  {
    num: '03',
    title: '一緒に歌える、仲間をつなぐ。',
    description: '歌声が重なれば、もっと楽しい。たくさんの「うた友」と出会って、笑顔あふれる場所をつくりたいな。',
    image: '/domily/domi-personality-03.webp',
  },
];

const MusicNoteIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="1.5"
    viewBox="0 0 24 24"
    className="w-5 h-5 text-[#4ADE80] opacity-60"
  >
    <path d="M9 18V5l12-2v13" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="16" r="3" />
  </svg>
);

export default function Domi({ setRoute }: DomiProps) {
  const worldRef = useRef<HTMLElement>(null);
  const [worldInView, setWorldInView] = useState(false);

  useEffect(() => {
    const el = worldRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setWorldInView(true);
      },
      { threshold: 0.25 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const goDomily = () => {
    setRoute('domily');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToWorld = () => {
    document.getElementById('world')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div id="domi-page" className="bg-[#0A0A0A] text-white font-sans antialiased overflow-x-hidden">
      {/* Hero */}
      <section className="relative min-h-[calc(100svh-72px)] flex items-center overflow-hidden bg-[#0A0A0A]">
        <div className="pointer-events-none absolute inset-0 z-0">
          <div className="absolute w-[400px] h-[400px] -top-[100px] -right-[50px] rounded-full bg-[rgba(74,222,128,0.12)] blur-[80px] animate-domi-float-slow" />
          <div className="absolute w-[300px] h-[300px] -bottom-[50px] -left-[50px] rounded-full bg-[rgba(0,245,212,0.08)] blur-[80px] animate-domi-float-slow-rev" />
        </div>

        <div className="relative z-[2] w-full max-w-[1200px] mx-auto px-6 py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-24 items-center">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-[520px]"
            >
              <span className="inline-block text-xs tracking-[0.28em] font-bold text-[#4ADE80] mb-4">
                MEET DOMI
              </span>
              <h1 className="text-3xl md:text-5xl font-black tracking-tight leading-tight mb-5">
                歌うことが、
                <br />
                <span className="bg-gradient-to-br from-[#4ADE80] to-[#00F5D4] bg-clip-text text-transparent">
                  もっと好きになる。
                </span>
              </h1>
              <p className="text-lg text-white/75 leading-[1.9] mb-10">
              みんなの歌が大好きなkoemoriの小さな仲間
              </p>
              <button
                type="button"
                onClick={scrollToWorld}
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-gradient-to-br from-[#4ADE80] to-[#00F5D4] text-[#0A0A0A] text-base font-semibold hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(74,222,128,0.3)] hover:brightness-105 transition-all cursor-pointer border-0"
              >
                Domiについて知る
                <ArrowRight className="w-5 h-5" />
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.55, delay: 0.08 }}
              className="relative aspect-square max-w-[480px] mx-auto w-full flex items-center justify-center"
            >
              <div className="absolute top-[10%] right-[5%] w-10 h-10 flex items-center justify-center rounded-full bg-[rgba(74,222,128,0.1)] border border-[rgba(74,222,128,0.2)] animate-domi-float-note">
                <MusicNoteIcon />
              </div>
              <div className="absolute bottom-[15%] left-0 w-10 h-10 flex items-center justify-center rounded-full bg-[rgba(74,222,128,0.1)] border border-[rgba(74,222,128,0.2)] animate-domi-float-note [animation-delay:1.5s]">
                <MusicNoteIcon />
              </div>
              <div className="absolute top-1/2 -right-[5%] w-10 h-10 flex items-center justify-center rounded-full bg-[rgba(74,222,128,0.1)] border border-[rgba(74,222,128,0.2)] animate-domi-float-note [animation-delay:2.5s]">
                <MusicNoteIcon />
              </div>
              <img
                src="/domily/domi-hero.webp"
                alt="Domi waving animation"
                className="relative z-10 block w-full max-w-[500px] max-h-[520px] object-contain"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* World — full-bleed banner */}
      <section
        ref={worldRef}
        id="world"
        className="relative overflow-hidden min-h-[560px] md:min-h-[580px]"
      >
        <div
          className={`absolute inset-0 z-0 bg-cover bg-no-repeat transition-transform duration-[2600ms] ease-[cubic-bezier(0.22,0.61,0.36,1)] origin-center ${
            worldInView ? 'scale-[1.055]' : 'scale-100'
          }`}
          style={{
            backgroundImage: "url('/domily/domi-world-banner.webp')",
            backgroundPosition: '58% center',
          }}
        />
        <div
          className="absolute inset-0 z-[1]"
          style={{
            background:
              'linear-gradient(90deg, rgba(5,10,28,0.86) 0%, rgba(5,10,28,0.64) 34%, rgba(5,10,28,0.30) 64%, rgba(5,10,28,0.18) 100%), linear-gradient(180deg, rgba(5,10,28,0.16) 0%, rgba(5,10,28,0.08) 52%, rgba(5,10,28,0.42) 100%)',
          }}
        />
        <div className="relative z-[2] w-full max-w-[1200px] mx-auto px-6 min-h-[560px] md:min-h-[580px] flex items-end md:items-center pb-[72px] md:pb-0 pt-[180px] md:pt-0">
          <div className="max-w-[640px]">
            <span className="inline-block text-[0.8125rem] font-semibold tracking-[0.12em] uppercase text-[#4ADE80] mb-4">
              DOMI&apos;S WORLD
            </span>
            <h2 className="text-[1.75rem] md:text-[2.25rem] font-bold leading-[1.35] mb-6 [text-shadow:0_3px_20px_rgba(0,0,0,0.5)]">
            音楽があふれる、小さな森
            </h2>
            <p className="text-[1.0625rem] text-white/[0.86] leading-[2] max-w-[600px] [text-shadow:0_2px_16px_rgba(0,0,0,0.42)]">
            koemoriは、歌声でつながる特別な場所。
            <br />
            誰かの声が誰かの心に届き、ここから新しいストーリーが生まれていきます。
            </p>
          </div>
        </div>
      </section>

      {/* Profile */}
      <section id="profile" className="relative bg-[#0A0A0A] py-16 md:py-32 overflow-hidden">
        <div className="w-full max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16 md:mb-24">
            <span className="inline-block text-[0.8125rem] font-semibold tracking-[0.12em] uppercase text-[#4ADE80] mb-4">
              CHARACTER PROFILE
            </span>
            <h2 className="text-[1.75rem] md:text-[2.25rem] font-bold leading-[1.35] mb-4">
              はじめまして、ドミだよ！
            </h2>
            <p className="text-[1.0625rem] text-white/75 max-w-[600px] mx-auto">
            歌うことが大好き！ みんなと一緒に歌えるのを、いつも楽しみにしているよ
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-24 items-center max-w-[1000px] mx-auto">
            <div className="group relative overflow-hidden rounded-3xl transition-transform duration-300 hover:-rotate-[1.2deg]">
              <img
                src="/domily/domi-fullbody.webp"
                alt="Domi CHARACTER PROFILE"
                className="relative z-[1] block w-full h-auto object-contain"
              />
              <div
                aria-hidden
                className="pointer-events-none absolute top-[-18%] left-0 z-[99] w-[34%] h-[136%] opacity-0 skew-x-[-18deg] group-hover:opacity-100 group-hover:animate-domi-profile-shine"
                style={{
                  background:
                    'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.14) 22%, rgba(255,255,255,0.88) 50%, rgba(255,255,255,0.14) 78%, rgba(255,255,255,0) 100%)',
                  filter: 'blur(1.5px)',
                }}
              />
            </div>

            <div className="flex flex-col gap-6">
              {profileItems.map((item) => (
                <div
                  key={item.label}
                  className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-2 sm:gap-6 py-6 border-b border-white/[0.06] last:border-b-0"
                >
                  <div className="flex items-center gap-2 text-sm font-semibold text-white/45">
                    <item.icon className="w-[18px] h-[18px] text-[#4ADE80] shrink-0" strokeWidth={1.5} />
                    {item.label}
                  </div>
                  <div className="text-base text-white/75 leading-[1.6]">{item.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Personality */}
      <section id="personality" className="relative bg-[#1A1F1C] py-16 md:py-32 overflow-hidden">
        <div className="w-full max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16 md:mb-24">
            <h2 className="text-[1.75rem] md:text-[2.25rem] font-bold leading-[1.35]">
            ドミの想い
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
            {personalityCards.map((card, index) => (
              <motion.div
                key={card.num}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, delay: index * 0.06 }}
                className={`flex flex-col items-center text-center rounded-3xl border border-white/[0.06] px-6 py-10 md:px-10 hover:-translate-y-1 hover:border-[rgba(74,222,128,0.2)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.3)] transition-all ${
                  index === 0
                    ? 'bg-gradient-to-b from-[rgba(74,222,128,0.05)] to-[#1A1A1A]'
                    : index === 1
                      ? 'bg-gradient-to-b from-[rgba(0,245,212,0.04)] to-[#1A1A1A]'
                      : 'bg-gradient-to-b from-[rgba(134,239,172,0.05)] to-[#1A1A1A]'
                }`}
              >
                <div className="w-full aspect-square max-w-[200px] mb-6 overflow-hidden rounded-2xl">
                  <img
                    src={card.image}
                    alt={`ドミ 性格${card.num}`}
                    className="w-full h-full object-contain object-center"
                  />
                </div>
                <span className="text-xs font-semibold tracking-[0.1em] text-[#4ADE80] mb-2">
                  {card.num}
                </span>
                <h3 className="text-xl font-bold leading-[1.5] mb-4">{card.title}</h3>
                <p className="text-[0.9375rem] text-white/75 leading-[1.8]">{card.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Message */}
      <section
        id="message"
        className="relative text-center py-16 md:py-32 overflow-hidden"
        style={{
          background:
            'radial-gradient(ellipse at 50% 0%, rgba(74,222,128,0.06) 0%, transparent 50%), #0A0A0A',
        }}
      >
        <div className="w-full max-w-[720px] mx-auto px-6">
          <div className="w-[112px] md:w-[160px] aspect-square mx-auto mb-6 md:mb-16 overflow-hidden rounded-full">
            <img
              src="/domily/domi-message.webp"
              alt="ドミ メッセージ"
              className="w-full h-full object-cover object-center rounded-full"
            />
          </div>

          <span className="inline-block text-[0.8125rem] font-semibold tracking-[0.12em] text-[#4ADE80] mb-6">
            A MESSAGE FROM DOMI
          </span>
          <h2 className="text-[1.75rem] md:text-4xl font-bold leading-[1.4] mb-10">
            ねえ、
            <span className="bg-gradient-to-br from-[#4ADE80] to-[#00F5D4] bg-clip-text text-transparent">
              今日は一緒に歌おう！
            </span>
          </h2>
          <p className="text-lg text-white/75 leading-[2.2] mb-10">
          うまく歌えなくたって大丈夫。 
歌うことが楽しいって気持ちが、いちばん大切なんだ。
ちょっぴり緊張する日もあるけれど、みんなと一緒ならきっと楽しいはず！
たくさんのうた友と一緒に、いろんな歌を歌えたらうれしいな。
ドミより
          </p>
          <div className="flex justify-center flex-wrap gap-6">
            <button
              type="button"
              onClick={goDomily}
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-gradient-to-br from-[#4ADE80] to-[#00F5D4] text-[#0A0A0A] text-base font-semibold hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(74,222,128,0.3)] hover:brightness-105 transition-all cursor-pointer border-0"
            >
              Domilyに戻る
              <ArrowLeft className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
